import React from 'react';
import * as mobx from 'mobx';
import * as mobxReact from 'mobx-react';
import { FormattedMessage, injectIntl, IntlShape } from 'react-intl';
import classnames from 'classnames';
import DefaultButton from '../../../../global/default-button/default-button';
import { FormField } from '../../../../global/form/form-field/form-field';
import Text from '../../../../global/text/text';
import { WarningIcon } from '../../../../global/icons/icons';
import { CopyButton } from '../../../../global/copy-button/copy-button';
import { getFormattedUrl } from '../../../../../infrastructure/utils/common.utils';
import { PreventJoinBefore } from './prevent-join-before';
import { WebsiteFooterLinkView } from './website-footer-links/website-footer-link.presentation';
import { WebsiteFooterLinkEdit } from './website-footer-links/website-footer-link-edit.presentation';
import { PassportField } from './passport-field/passport-field';
import { WebsiteFormBodyVM } from './website-form-body.viewmodel';
import './website-form-body.scss';
import { PagesNavigation } from './passport-pages/pages-navigation.container';
import { hasPermission } from '../../../../../infrastructure/utils/has-permissions';
import { Permission } from '../../../../../api/roles.dao';
import { InfoPopover } from '../../../../global/info-popover/info-popover';
import {
    CONFIGURE_REGISTRATION_FORM_TEXT,
    CUSTOM_CSS_APPLIED_TEXT,
    CUSTOM_CSS_TEXT,
    EMAIL_DISPLAY_NAME_INFO_MESSAGE_TEXT,
    EMAIL_DISPLAY_NAME_TEXT,
    ENTER_DISPLAY_NAME_TEXT,
    ENTER_LABEL_FOR_MEETING_LINKS_SECTION_TEXT,
    LABEL_FOR_MEETING_LINKS_SECTION_TEXT,
    MEETING_DETAILS_PAGE_TEXT,
    NO_CUSTOM_CSS_APPLIED_TEXT,
    NO_SELECTED_URL_TEXT,
    PAGES_IN_TOP_NAVIGATION_PUBLIC_TEXT,
    PAGES_IN_TOP_NAVIGATION_SIGN_IN_REQUIRED_TEXT,
    PASSPORT_EMAILS_TEXT,
    PASSPORT_RESPONSIVE_EMAILS_TEXT,
    PASSPORT_SETTINGS_FIELDS_TEXT,
    PASSPORT_SETTINGS_TEXT,
    RESTRICTED_PASSPORT_SETTINGS_FIELDS_TEXT,
    RESTRICTED_PASSPORT_SETTINGS_TEXT,
    SAVE_WEBSITE_TEXT,
    SAVED_WEBSITE_TEXT,
    SCHEDULED_EMAILS_TEXT,
    WEBSITE_CONFIGURE_TEXT,
} from './website-form.texts';
import { PASSPORT_URL_TEXT } from '../edit-website.texts';
import { CONFIGURE_TEXT, openText } from '../../../../../constants/common-labels.constants';
import { LinkTargetValue } from '../../../../../constants/common.constants';
import { URL_REDIRECTION_DISABLED_TEXT, URL_REDIRECTION_ENABLED_TEXT, URL_REDIRECTION_INFO_TEXT } from './url-redirection-form/url-redirection-form.texts';

interface IProps {
    vm: WebsiteFormBodyVM;
    intl: IntlShape;
}

const emailDisplayNameInfoMessage = <FormattedMessage {...EMAIL_DISPLAY_NAME_INFO_MESSAGE_TEXT} />;

const WebsiteFormBody = injectIntl(
    mobxReact.observer((props: IProps) => {
        const { vm, intl } = props;

        if (vm) {
            const passportTabPermission = hasPermission(Permission.PASSPORT_TAB);
            const editEventTitleAndDescriptioPermission = hasPermission(Permission.EDIT_EVENT_TITLE_AND_DESCRIPTION);
            const subdomain = vm.getSubdomain();
            const accountSubdomain = vm.getAccountSubdomain();
            const formattedSubdomain = getFormattedUrl(subdomain, undefined, accountSubdomain);
            return (
                <div className='oe-form__body passport-form'>
                    <>
                        {subdomain
                            ? <PassportField
                                labelText={intl.formatMessage(PASSPORT_URL_TEXT)}
                                rowText={formattedSubdomain}
                                className='passport-form__subdomain-wrapper'
                            >
                                <div className='passport-form__buttons-block'>
                                    <DefaultButton
                                        modifier='slim'
                                        text={openText()}
                                        disabled={!subdomain}
                                        onClick={() => window.open(formattedSubdomain, LinkTargetValue.BLANK)}
                                    />
                                    <CopyButton
                                        withoutIcon
                                        copyValue={formattedSubdomain}
                                        disabled={!subdomain}
                                    />
                                </div>
                            </PassportField>
                            : <PassportField
                                labelText={intl.formatMessage(PASSPORT_URL_TEXT)}
                                onClick={vm.showPassportSettingsPopup}
                                buttonText={passportTabPermission &&
                                    intl.formatMessage(CONFIGURE_TEXT)}
                                rowTextPrefix={<WarningIcon className='warning m-right-10px' />}
                                rowText={intl.formatMessage(NO_SELECTED_URL_TEXT)}
                                disabled={vm.isEditPassportTitlePopupOpened()}
                            />
                        }
                        {passportTabPermission && <FormField className='m-bottom-0px'>
                            <PassportField
                                rowText={intl.formatMessage(vm.urlRedirectionEnabled ? URL_REDIRECTION_ENABLED_TEXT : URL_REDIRECTION_DISABLED_TEXT)}
                                rowTextSuffix={
                                    <InfoPopover className='m-left-10px'>
                                        <div>{intl.formatMessage(URL_REDIRECTION_INFO_TEXT)}</div>
                                    </InfoPopover>
                                }
                                onClick={vm.showUrlRedirectionPopup}
                                buttonText={intl.formatMessage(CONFIGURE_TEXT)}
                                className='passport-settings-field'
                            />
                        </FormField>}
                        {editEventTitleAndDescriptioPermission && <FormField className='m-top-15px m-bottom-0px'>
                            <PassportField
                                labelText={intl.formatMessage(RESTRICTED_PASSPORT_SETTINGS_TEXT)}
                                onClick={vm.showEditPassportTitlePopup}
                                buttonText={intl.formatMessage(CONFIGURE_TEXT)}
                                rowText={intl.formatMessage(RESTRICTED_PASSPORT_SETTINGS_FIELDS_TEXT)}
                                className='passport-settings-field'
                                disabled={vm.isPassportSettingsPopupOpened()}
                            />
                        </FormField>}
                    </>
                    {passportTabPermission &&
                        <div>
                            <FormField className='m-top-15px m-bottom-0px'>
                                <PassportField
                                    labelText={intl.formatMessage(PASSPORT_SETTINGS_TEXT)}
                                    onClick={vm.showPassportSettingsPopup}
                                    buttonText={intl.formatMessage(CONFIGURE_TEXT)}
                                    rowText={intl.formatMessage(PASSPORT_SETTINGS_FIELDS_TEXT)}
                                    className='passport-settings-field'
                                    disabled={vm.isEditPassportTitlePopupOpened()}
                                />
                            </FormField>
                            <FormField className='m-top-15px m-bottom-0px'>
                                <div className='passport-field-container passport-settings-field w-100'>
                                    <div className={classnames('passport-field-container__row')}>
                                        <div>
                                            <Text
                                                message={intl.formatMessage(PAGES_IN_TOP_NAVIGATION_SIGN_IN_REQUIRED_TEXT)}
                                                className='passport-field-container__row-text'
                                            />
                                        </div>
                                    </div>
                                    <div className={classnames('passport-field-container__row')}>
                                        <div className='p-top-10px'>
                                            <PagesNavigation vm={vm} intl={intl} />
                                        </div>
                                        <DefaultButton
                                            text={intl.formatMessage(WEBSITE_CONFIGURE_TEXT)}
                                            modifier='slim'
                                            onClick={vm.showConfPassNavPopup}
                                            className='pages-navigation_configure m-top-10px'
                                        />
                                    </div>
                                </div>
                            </FormField>
                            <FormField className='m-top-15px m-bottom-0px'>
                                <div className={`passport-field-container passport-settings-field w-100`}>
                                    <div className={classnames('passport-field-container__row')}>
                                        <div>
                                            <Text
                                                message={intl.formatMessage(PAGES_IN_TOP_NAVIGATION_PUBLIC_TEXT)}
                                                className='passport-field-container__row-text'
                                            />
                                        </div>
                                    </div>
                                    <div className={classnames('passport-field-container__row')}>
                                        <div className='p-top-10px'>
                                            <PagesNavigation vm={vm} intl={intl} isPublicNavigation />
                                        </div>
                                    </div>
                                </div>
                            </FormField>

                            <PassportField
                                labelText={intl.formatMessage(MEETING_DETAILS_PAGE_TEXT)}
                                additionalField={
                                    <div className='m-bottom-15px'>
                                        <div className='passport-field-container__additional-field meeting-links-section'>
                                            <FormField
                                                className='m-bottom-0px'
                                                label={intl.formatMessage(LABEL_FOR_MEETING_LINKS_SECTION_TEXT)}>
                                                <div className='passport-field-container__additional-field-block'>
                                                    <div className='passport-field-container__additional-field-block-item'>
                                                        <input
                                                            type='text'
                                                            className='oe-form-field__control'
                                                            value={vm.meetingLinksLabel}
                                                            onChange={mobx.action(({ target: { value } }) => {
                                                                if (vm.disableMeetingLinksLabel)
                                                                    vm.setDisableMeetingLinksLabel(false);
                                                                vm.setMeetingLinksLabel(value);
                                                            })}
                                                            placeholder={intl.formatMessage(ENTER_LABEL_FOR_MEETING_LINKS_SECTION_TEXT)}
                                                        />
                                                    </div>
                                                    <DefaultButton
                                                        text={intl.formatMessage(!vm.disableMeetingLinksLabel ? SAVE_WEBSITE_TEXT : SAVED_WEBSITE_TEXT)}
                                                        modifier='slim'
                                                        onClick={vm.saveMeetingLinksLabel}
                                                        disabled={vm.disableMeetingLinksLabel || !!vm.getMeetingLinksLabelError()}
                                                    />
                                                </div>
                                            </FormField>
                                        </div>
                                        {vm.getMeetingLinksLabelError() && <span className='oe-form-field__error'>{vm.getMeetingLinksLabelError()}</span>}
                                    </div>
                                }
                            />
                            <PreventJoinBefore {...vm.composePreventJoinBeforeProps()} />
                            <PassportField
                                onClick={vm.showFormRegistrationFormPopup}
                                buttonText={intl.formatMessage(WEBSITE_CONFIGURE_TEXT)}
                                rowText={intl.formatMessage(CONFIGURE_REGISTRATION_FORM_TEXT)}
                            />
                            <PassportField
                                labelText={intl.formatMessage(PASSPORT_EMAILS_TEXT)}
                                onClick={vm.showEmailsTemplateForm}
                                buttonText={intl.formatMessage(CONFIGURE_TEXT)}
                                rowText={intl.formatMessage(PASSPORT_RESPONSIVE_EMAILS_TEXT)}
                                additionalField={
                                    <div className='m-bottom-15px'>
                                        <div className='passport-field-container__additional-field'>
                                            <FormField
                                                className='m-bottom-0px'
                                                label={intl.formatMessage(EMAIL_DISPLAY_NAME_TEXT)
                                                }>
                                                <div className='passport-field-container__additional-field-block'>
                                                    <div className='passport-field-container__additional-field-block-item'>
                                                        <InfoPopover>
                                                            <div>{emailDisplayNameInfoMessage}</div>
                                                        </InfoPopover>
                                                        <input
                                                            type='text'
                                                            className='oe-form-field__control'
                                                            value={vm.emailDisplayName}
                                                            onChange={mobx.action(({ target: { value } }) => {
                                                                if (vm.disableEmailDisplayName)
                                                                    vm.setDisableEmailDisplayName(false);
                                                                vm.setEmailDisplayName(value);
                                                            })}
                                                            placeholder={intl.formatMessage(ENTER_DISPLAY_NAME_TEXT)}
                                                        />
                                                    </div>
                                                    <DefaultButton
                                                        text={intl.formatMessage(!vm.disableEmailDisplayName ? SAVE_WEBSITE_TEXT : SAVED_WEBSITE_TEXT)}
                                                        modifier='slim'
                                                        onClick={vm.saveEmailAddressDisplayName}
                                                        disabled={vm.disableEmailDisplayName || !!vm.getEmailDisplayNameError()}
                                                    />
                                                </div>
                                            </FormField>
                                        </div>
                                        {vm.getEmailDisplayNameError() && <span className='oe-form-field__error'>{vm.getEmailDisplayNameError()}</span>}
                                    </div>
                                }
                            />

                            {passportTabPermission &&
                                <PassportField
                                    onClick={vm.showScheduledEmails}
                                    buttonText={intl.formatMessage(CONFIGURE_TEXT)}
                                    rowText={intl.formatMessage(SCHEDULED_EMAILS_TEXT)}
                                />}
                            <PassportField
                                labelText={intl.formatMessage(CUSTOM_CSS_TEXT)}
                                onClick={() => vm.showCustomCssForm(intl)}
                                buttonText={intl.formatMessage(CONFIGURE_TEXT)}
                                rowText={intl.formatMessage(vm.getCustomCss() ? CUSTOM_CSS_APPLIED_TEXT : NO_CUSTOM_CSS_APPLIED_TEXT)}
                            />
                            <div className='m-top-15px'>
                                {vm.getFooterLinks().map((value, index) => (
                                    <WebsiteFooterLinkView
                                        footerLink={value}
                                        deleteFooterLink={() => vm.removeFooterLinkByValue(value)}
                                        key={index}
                                    />
                                ))}
                                {vm.getFooterLinkFieldStatus() && (
                                    <WebsiteFooterLinkEdit
                                        data={vm.getFooterLink()}
                                        changeFooterLinkLabel={e => vm.setFooterLinkLabel(e.target.value)}
                                        changeFooterLinkUrl={e => vm.setFooterLinkUrl(e.target.value)}
                                        getNewFooterLinkUrlValidationError={vm.getFooterLinkUrlValidationError()}
                                        getNewFooterLinkLabelValidationError={vm.getFooterLinkLabelValidationError()}
                                    />
                                )}
                                <DefaultButton
                                    className='w-100 m-top-15px button-padding'
                                    text={
                                        <FormattedMessage
                                            id='oe.footer.addLink'
                                            defaultMessage='{prefix}Add Footer Link'
                                            description='Button text'
                                            values={{
                                                prefix: '+ ',
                                            }}
                                        />
                                    }
                                    modifier='options'
                                    onClick={vm.addNewFooterLink}
                                />
                            </div>
                        </div>
                    }
                </div>
            );
        }
    })
);

export { WebsiteFormBody };
